// Registro dos tipos de entrada suportados pelo fluxo unificado (POST /internals/entryrecord/).
// Cada chave aqui precisa corresponder exatamente ao `code` de um TypeEntry cadastrado no backend
// (GET /internals/typeentry/). Tipos que existem no backend mas não estão aqui simplesmente não
// aparecem no seletor "Tipo de Entrada",
// que ainda não está pronto.
//
// COMO ADICIONAR UM NOVO TIPO DE ENTRADA :
// 1. Confirmar com o backend o `code` exato já cadastrado no TypeEntry. Não dá pra confiar no
//    campo `body_schema` do TypeEntry pra descobrir os campos automaticamente — hoje ele vem
//    vazio ({}) em homolog/produção. Pedir a lista de campos direto pro backend/product owner.
// 2. Decidir o `formVariant`:
//    - 'wizard-cadastro': fluxo completo (dados pessoais + foto + ficha de inclusão + upload
//      assinado). Usado por `entrada_sistema`, `descumprimento_regime_aberto`,
//      `prisao_durante_fuga` e `entrada_monitoramento`. Ver `WizardCadastroSection` em
//      unifiedEntry.js.
//    - 'retorno-simples': busca de interno existente por CPF/nome + pavilhão/cela. Usado pelos 3
//      retornos (fuga, saída temporária, internação médica). Ver o bloco `selectedVariant ===
//      'retorno-simples'` em unifiedEntry.js.
//    - Se nenhum dos dois servir (ex: um tipo com campos totalmente diferentes), criar um novo
//      valor de `formVariant` aqui e implementar o bloco JSX correspondente em unifiedEntry.js
//      (procurar por `selectedVariant ===` no arquivo).
// 3. Adicionar a entrada abaixo com `label` (texto exibido no seletor) e `formVariant`.
// 4. Se o tipo usa 'retorno-simples' mas precisa de campos além de pavilhão/cela (como o
//    `retorno_fulga`, que também pede conduta/data do retorno/dias limite), declarar em
//    `extraFields` — cada valor precisa ter um bloco correspondente em unifiedEntry.js
//    (procurar por `extraFields.includes(`).
// 5. Preencher `description` com a regra de negócio: em que momento esse tipo se aplica (não o
//    fluxo/campos). Aparece como legenda curta no cartão de seleção (ver
//    `unified-entry__type-description` em unifiedEntry.js/internos.css) pra ajudar a escolher o
//    tipo certo. Sem truncamento — manter a frase curta (uma linha) pra não esticar o cartão.
export const ENTRY_TYPE_CONFIG = {
  entrada_sistema: {
    formVariant: 'wizard-cadastro',
    label: 'Cadastro novo / Nova entrada',
    description: 'Primeiro cadastro de um novo interno, ou nova entrada de um interno já cadastrado.',
  },
  retorno_fulga: {
    formVariant: 'retorno-simples',
    label: 'Retorno de Fuga',
    // O antigo endpoint `internals/internal/recapture/` usava `restart_date`, mas o entryrecord
    // novo espera `return_date` (mesmo campo usado pelos outros retornos abaixo).
    extraFields: ['conduct', 'return_date', 'limit_days'],
    description: 'Interno foragido, recapturado por causa da própria fuga (não por outro motivo).',
  },
  retorno_saida_temporaria: {
    formVariant: 'retorno-simples',
    label: 'Retorno de Saída Temporária',
    // Data do retorno. Usa `return_date` (não `entry_date`) porque é o mesmo nome de campo do
    // LeavingRecord sendo fechado (confirmado olhando a resposta real do retrieve em homolog) e
    // o mesmo nome que o antigo endpoint `hospitalreturn` já usava.
    extraFields: ['return_date'],
    // Pré-carrega pavilhão/cela com a cela de origem (de onde o interno saiu), igual o antigo
    // botão de ícone na tabela já fazia — ver `getLastPendingTemporaryLeavingRecord` em
    // unifiedEntry.js. Só faz sentido pra tipos que fecham uma saída temporária.
    prefillOriginCell: true,
    description: 'Retorno voluntário da saída temporária, dentro do prazo.',
  },
  retorno_internacao_externa: {
    formVariant: 'retorno-simples',
    label: 'Retorno de Internação Médica',
    extraFields: ['return_date'],
    description: 'Interno retornando à unidade após internação médica externa.',
  },
  // Interno que estava em Saída Temporária, mas foi preso por outro motivo (não foi um
  // retorno voluntário) — mesmo status atual/final e mesmo leaving de retorno_saida_temporaria,
  // mas é um TypeEntry (e um botão) separado pra registrar esse caso especificamente.
  prisao_em_saida_temporaria: {
    formVariant: 'retorno-simples',
    label: 'Prisão em Saída Temporária',
    extraFields: ['return_date'],
    prefillOriginCell: true,
    description: 'Estava em saída temporária, mas foi preso por outro motivo.',
  },
  descumprimento_regime_aberto: {
    formVariant: 'wizard-cadastro',
    label: 'Descumprimento no Regime Aberto',
    description: 'Interno em liberdade que descumpriu o regime aberto.',
  },
  // Interno que estava foragido, mas foi preso por outro motivo (não foi recapturado
  // especificamente pela fuga) — diferente do `retorno_fulga` (busca interno existente por
  // CPF/nome + pavilhão/cela), esse tipo pede o fluxo completo de novo (foto + dados + ficha de
  // inclusão), igual `entrada_sistema`.
  prisao_durante_fuga: {
    formVariant: 'wizard-cadastro',
    label: 'Prisão durante Fuga',
    description: 'Estava foragido, mas foi preso por outro motivo (não foi recaptura).',
  },
  entrada_monitoramento: {
    formVariant: 'wizard-cadastro',
    label: 'Entrada para Monitoramento',
    description: 'Uso exclusivo do monitoramento eletrônico.',
  },
};
